import { Drawer, Card, CardHeader, CardContent, useMediaQuery, Grid, Divider, List, ListItem, Typography, Box, Tooltip, Select, MenuItem, Button, FormHelperText, Stack, InputLabel, FormControl } from "@mui/material";
import { useListContext, crudGetOne, useSaveContext } from "ra-core";
import { FC, useState } from "react";
import { useDispatch } from "react-redux";
import { crudGetOneEx } from "@/actions/crudFetchEx";
import ReadonlyField from "@/components/input/ReadonlyField";
import CreateOrEdit from "@/controller/CreateOrEdit";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { ClientManageDto, ShareSecretResponse } from "@/gen/api/user";
import { autoCopyTextFieldProps } from "@/utils/clipboard";


export interface ResetSharedSecretProps {
  clear: () => void;
  client: ClientManageDto|null;
}

const ResetSharedSecret: FC<ResetSharedSecretProps> = (props) => {
  const { client } = props
  const handlers = useDefaultSavedHandlers("Reset Client Secret")
  return (
    <CreateOrEdit resource="settings-client-share-secret" basePath="" id={client?.clientId || ''} record={client}
      {...handlers}
    >
      <ResetSharedSecretBody {...props} />
    </CreateOrEdit>
  )
}

const ResetSharedSecretBody: FC<ResetSharedSecretProps> = ({
  clear,
  client,
}) => {
  const [states, setStates] = useState({
    creationResult: null as ShareSecretResponse|null,
    copied: false,
    gracefulRetirement: '--',
    error: '',
  })
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480
  
  const save = useSimpleSaveAction()
  const { saving } = useSaveContext()
  const { refetch } = useListContext()

  const performReset = () => {
    if (saving) { return }
    setStates(v => v.error ? ({...v, error: '' }): v)
    save(states.gracefulRetirement as any, {
      onSuccess: ({ data }: {data:ShareSecretResponse}) => {
        setStates(v => ({...v, creationResult: data }))
      },
      onFailure: (err: any) => {
        setStates(v => ({...v, error: err.message }))
      },
    })
  }
  
  const dispatch = useDispatch()
  const handleClose = (e:any, reason?:string): void => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') { return }
    clear();
    if (states.creationResult) {
      dispatch(crudGetOneEx('settings-client-share-secret', states.creationResult.clientId || '', ''))
      setStates(v => ({...v, creationResult: null, gracefulRetirement: '--', error: ''}))
      refetch()
    }
  };

  return (
    <Drawer
      anchor="right"
      // onOpen={() => {}}
      ModalProps={{
        onClose: handleClose
      }}
      onClose={() => {}}
      open={!!client}
      PaperProps={{ sx: { p: 2, width: width, } }}
    >
      <Card sx={{ overflowY: 'auto', mb: 4, }} >
        <CardHeader title="Reset Client Secret" />
        <CardContent>
          <Grid container spacing={4} >
            <Grid item xs={12}>
              <List>
                <ListItem>
                  <Typography variant="h6" >{client?.clientName}</Typography>
                </ListItem>
                {client?.remark &&
                  <>
                    <Divider />
                    <ListItem>
                      <Typography variant="body2">
                        <Typography variant="body1" component="span">Remark: </Typography>
                        {client?.remark}
                      </Typography>
                    </ListItem>
                  </>
                }
              </List>
            </Grid>
            <Grid item xs={12}>
              <Tooltip title={states.copied ? 'Copied' : 'Click to Copy'}>
                <Box>
                  <ReadonlyField 
                    id="client-id-generated"
                    label="Client Id"
                    value={client?.clientId || ''}
                    {...autoCopyTextFieldProps(
                      () => setStates(v => ({...v, copied: true})), 
                      () => setTimeout(() => setStates(v => ({...v, copied: false})), 500)
                    )}
                    {...{inputProps: {tabIndex: 0}}}
                  />
                </Box>
              </Tooltip>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel htmlFor="graceful-retire" id="graceful-retire-label">Invalidate Previous Client Secret</InputLabel>
                <Select
                  id="graceful-retire"
                  fullWidth
                  disabled={!!states.creationResult}
                  label="Invalidate Previous Client Secret"
                  value={states.gracefulRetirement}
                  onChange={(e) => { setStates(v => ({...v, gracefulRetirement: e.target.value}))}}
                >
                  <MenuItem value="--">Immediately</MenuItem>
                  <MenuItem value="00:30:00">In 30 minutes</MenuItem>
                  <MenuItem value="01:00:00">In 1 hour</MenuItem>
                  <MenuItem value="03:00:00">In 3 hours</MenuItem>
                  <MenuItem value="06:00:00">In 6 hours</MenuItem>
                  <MenuItem value="1.00:00:00">In 1 day</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {states.error && (
              <Grid item xs={12}>
                <FormHelperText error>
                  {states.error}
                </FormHelperText>
              </Grid>
            )}
            {states.creationResult &&
              <>
                <Grid item xs={12}>
                  <Tooltip title={states.copied ? 'Copied' : 'Click to Copy'}>
                    <Box>
                      <ReadonlyField 
                        id="client-secret-generated"
                        label="New Client Secret"
                        value={states.creationResult.clientSecret || ''}
                        helperText="Please copy this ClientSecret and save it to somewhere safe. OnData will only display it for once."
                        {...autoCopyTextFieldProps(
                          () => setStates(v => ({...v, copied: true})), 
                          () => setTimeout(() => setStates(v => ({...v, copied: false})), 500)
                        )}
                        {...{inputProps: {tabIndex: 0}}}
                      />
                    </Box>
                  </Tooltip>
                </Grid>
              </>
            }
          </Grid>
          <Stack 
            direction="row"
            justifyContent="space-between"
            sx={{ py: 2 }} 
          >
            <Button
              color="primary"
              disabled={saving}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            {!states.creationResult &&
              <Button
                color="primary"
                disabled={saving}
                variant="contained"
                onClick={performReset}
              >
                Reset
              </Button>
            }
          </Stack>
        </CardContent>
      </Card>
    </Drawer>
  )
}

export default ResetSharedSecret;
